import { Head, Link, router } from "@inertiajs/react"
import {
  Plus,
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Search,
  QrCode,
  ExternalLink,
  ScanLine,
} from "lucide-react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import {
  coffeesPath,
  newCoffeePath,
  editCoffeePath,
  coffeePath,
  qrCoffeePath,
} from "@/routes"
import type { BreadcrumbItem, PublishedCoffeeCard } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Props {
  coffees: PublishedCoffeeCard[]
  search?: string
}

const statusColors: Record<string, string> = {
  draft:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  published:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  archived: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
}

export default function CoffeesIndex({ coffees, search }: Props) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState(search || "")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [coffeeToDelete, setCoffeeToDelete] = useState<number | null>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: t("coffees.breadcrumb", "My Coffees"), href: coffeesPath() },
  ]

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      router.get(
        coffeesPath(),
        { search: searchQuery },
        { preserveState: true, preserveScroll: true, only: ["coffees"] },
      )
    }, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const handleDelete = () => {
    if (coffeeToDelete) {
      router.delete(coffeePath(coffeeToDelete), {
        onSuccess: () => {
          setDeleteModalOpen(false)
          setCoffeeToDelete(null)
        },
      })
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("coffees.title", "My Coffees")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div>
          <p className="text-muted-foreground mb-2 text-sm">
            {t("coffees.breadcrumb", "My Coffees")}
          </p>
          <h1 className="text-3xl font-bold">
            {t("coffees.title", "My Coffees")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t(
              "coffees.subtitle",
              "Create product pages for your coffee bags with QR codes and AI-generated content.",
            )}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="mb-6 flex items-center justify-between">
              <Link href={newCoffeePath()}>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("coffees.new_coffee", "New Coffee Page")}
                </Button>
              </Link>

              <div className="relative w-64">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder={t("coffees.search", "Search coffees...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {coffees.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="bg-muted mb-4 rounded-full p-4">
                  <QrCode className="text-muted-foreground h-8 w-8" />
                </div>
                <h3 className="mb-1 text-lg font-semibold">
                  {t("coffees.empty_title", "No coffee pages yet")}
                </h3>
                <p className="text-muted-foreground mb-4 max-w-sm">
                  {t(
                    "coffees.empty_description",
                    "Create your first product page from a cupping session. Add a QR code to your coffee bags and share detailed profiles with consumers.",
                  )}
                </p>
                <Link href={newCoffeePath()}>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    {t("coffees.create_first", "Create your first coffee page")}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {coffees.map((coffee) => (
                  <Card
                    key={coffee.id}
                    className="group relative overflow-hidden transition-shadow hover:shadow-md"
                  >
                    <CardContent className="p-5">
                      <div className="mb-3 flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-base font-semibold">
                            {coffee.name}
                          </h3>
                          {coffee.sample_origin && (
                            <p className="text-muted-foreground mt-0.5 truncate text-sm">
                              {coffee.sample_origin}
                              {coffee.sample_name && ` · ${coffee.sample_name}`}
                            </p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link href={editCoffeePath(coffee.id)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                {t("common.edit", "Edit")}
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link href={qrCoffeePath(coffee.id)}>
                                <QrCode className="mr-2 h-4 w-4" />
                                {t("coffees.qr_code", "QR Code")}
                              </Link>
                            </DropdownMenuItem>
                            {coffee.status === "published" && (
                              <DropdownMenuItem asChild>
                                <a
                                  href={`/c/${coffee.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="mr-2 h-4 w-4" />
                                  {t("coffees.view_public", "View Public Page")}
                                </a>
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => {
                                setCoffeeToDelete(coffee.id)
                                setDeleteModalOpen(true)
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t("common.delete", "Delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="mb-3 flex items-center gap-2">
                        <Badge
                          className={statusColors[coffee.status] || ""}
                          variant="outline"
                        >
                          {t(`coffees.status_${coffee.status}`, coffee.status)}
                        </Badge>
                        {coffee.cupping_form && (
                          <Badge variant="secondary" className="text-xs">
                            {coffee.cupping_form.toUpperCase()}
                          </Badge>
                        )}
                      </div>

                      <div className="text-muted-foreground flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3.5 w-3.5" />
                          {coffee.views_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <ScanLine className="h-3.5 w-3.5" />
                          {coffee.qr_scans_count}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("coffees.delete_title", "Delete Coffee Page")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "coffees.delete_description",
                "Are you sure you want to delete this coffee page? This action cannot be undone.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {t("common.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
